import { useRef, type PointerEvent as ReactPointerEvent } from 'react'
import { useCanvasStore } from './canvas-store'
import type { Point } from './geometry'

type DragOptions = {
  itemId: string
  // The item's last-committed world position. Used as the anchor for delta math.
  basePosition: Point
  // Called on pointer-up. Receives the final position so the parent can persist via tRPC.
  // Not called if the pointer barely moved (treated as a click).
  onCommit: (position: Point) => Promise<void> | void
  // Called on pointer-down before drag tracking starts. Used to drive selection — every
  // pointer-down on an item makes it the active selection. Caller receives the event so
  // it can read shift/meta for multi-select.
  onSelect?: (e: ReactPointerEvent<HTMLElement>) => void
  // Candidate items that should move together with this one when they're part of the
  // current multi-selection. Filtered by the hook against the live selection on
  // pointer-down so callers don't need to read selection state themselves.
  linkedItems?: Array<{ id: string; position: Point }>
  // Threshold in screen pixels under which we treat the gesture as a click, not a drag.
  // Avoids unnecessary tRPC writes when a user just clicks an item.
  clickThresholdPx?: number
  // Disables drag entirely while true (e.g. while inline editing text content).
  disabled?: boolean
}

// Pointer-driven drag for canvas items. Adjusts deltas by the current scale so the item
// moves 1:1 with the cursor in screen space, even at zoom levels other than 1.
//
// The Zustand store holds the in-flight drag override; on commit we hand the final world
// position back to the caller and clear the override so the rendered position falls back
// to the server-confirmed value.
export function useDragInteraction({
  itemId,
  basePosition,
  onCommit,
  onSelect,
  linkedItems,
  clickThresholdPx = 4,
  disabled,
}: DragOptions) {
  const beginDrag = useCanvasStore((s) => s.beginDrag)
  const updateDrag = useCanvasStore((s) => s.updateDrag)
  const endDrag = useCanvasStore((s) => s.endDrag)
  const stateRef = useRef<{
    startScreen: Point
    startWorld: Point
    moved: boolean
    linked: Array<{ id: string; basePosition: Point }>
  } | null>(null)

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (disabled) return
    if (e.button !== 0) return
    e.stopPropagation()
    onSelect?.(e)
    // Shift-click is a selection toggle, not a drag. Bail before entering drag mode so
    // the click can add/remove the item without moving it (and without capturing the
    // pointer, which would otherwise swallow the gesture).
    if (e.shiftKey) return
    const selected = useCanvasStore.getState().selectedIds
    const linked: Array<{ id: string; basePosition: Point }> = []
    if (linkedItems && selected.has(itemId) && selected.size > 1) {
      for (const li of linkedItems) {
        if (li.id !== itemId && selected.has(li.id)) {
          linked.push({ id: li.id, basePosition: li.position })
        }
      }
    }
    stateRef.current = {
      startScreen: { x: e.clientX, y: e.clientY },
      startWorld: { ...basePosition },
      moved: false,
      linked,
    }
    beginDrag(itemId)
    updateDrag(itemId, basePosition)
    for (const l of linked) {
      beginDrag(l.id)
      updateDrag(l.id, l.basePosition)
    }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: ReactPointerEvent<HTMLElement>) => {
    const st = stateRef.current
    if (!st) return
    const scale = useCanvasStore.getState().scale
    const sdx = e.clientX - st.startScreen.x
    const sdy = e.clientY - st.startScreen.y
    if (!st.moved && Math.hypot(sdx, sdy) > clickThresholdPx) {
      st.moved = true
    }
    const dxWorld = sdx / scale
    const dyWorld = sdy / scale
    updateDrag(itemId, {
      x: st.startWorld.x + dxWorld,
      y: st.startWorld.y + dyWorld,
    })
    for (const l of st.linked) {
      updateDrag(l.id, {
        x: l.basePosition.x + dxWorld,
        y: l.basePosition.y + dyWorld,
      })
    }
  }

  const onPointerUp = (e: ReactPointerEvent<HTMLElement>) => {
    const st = stateRef.current
    if (!st) return
    e.currentTarget.releasePointerCapture(e.pointerId)
    const override = useCanvasStore.getState().dragOverrides[itemId]
    const linked = st.linked
    stateRef.current = null
    const clearAll = () => {
      endDrag(itemId)
      for (const l of linked) endDrag(l.id)
    }
    if (!st.moved || !override) {
      clearAll()
      return
    }
    const finalPosition = override.position
    // Caller reads the override map (including linked items' positions) to persist the
    // whole multi-selection in a single tRPC update. We keep the overrides set until
    // commit resolves so nothing snaps back mid-flight.
    void Promise.resolve(onCommit(finalPosition)).finally(clearAll)
  }

  return { onPointerDown, onPointerMove, onPointerUp }
}
